import { useReducer, useState, useEffect, useContext, useCallback } from 'react'
import useSound from 'use-sound'
import notifySound from '../../assets/chat_notify.mp3'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { makeRandomId } from '../../helpers/makeRandomId'
import { resolveNotificationSoundUrls } from '../../utils/notificationSounds'

const LOAD_ITEMS = 'LOAD_ITEMS'
const UPDATE_ITEM = 'UPDATE_ITEM'
const DELETE_ITEM = 'DELETE_ITEM'
const SORT_ITEMS = 'SORT_ITEMS'
const RESET = 'RESET'

const sortByUnread = items => {
  return items.sort((a, b) => {
    const aUnread =
      a.users && a.users.length > 0
        ? a.users.find(u => u.unreads > 0)?.unreads || 0
        : a.unreads || a.unreadMessages || 0

    const bUnread =
      b.users && b.users.length > 0
        ? b.users.find(u => u.unreads > 0)?.unreads || 0
        : b.unreads || b.unreadMessages || 0

    if (aUnread !== bUnread) return bUnread - aUnread
    if (a.updatedAt && b.updatedAt)
      return new Date(b.updatedAt) - new Date(a.updatedAt)

    const aName = a.name || a.title || ''
    const bName = b.name || b.title || ''
    return aName.localeCompare(bName)
  })
}

const reducer = (state, action) => {
  switch (action.type) {
    case LOAD_ITEMS: {
      const items = action.payload
      const newItems = []
      let newState = [...state]
      items.forEach(item => {
        const itemIndex = newState.findIndex(i => i.id === item.id)
        if (itemIndex !== -1) newState[itemIndex] = item
        else newItems.push(item)
      })
      return sortByUnread([...newState, ...newItems])
    }
    case UPDATE_ITEM: {
      const item = action.payload
      const itemIndex = state.findIndex(i => i.id === item.id)
      if (itemIndex !== -1) {
        const newState = [...state]
        newState[itemIndex] = item
        return sortByUnread(newState)
      }
      return sortByUnread([item, ...state])
    }
    case DELETE_ITEM:
      return state.filter(item => item.id !== action.payload)
    case SORT_ITEMS:
      return sortByUnread([...state])
    case RESET:
      return []
    default:
      return state
  }
}

// Action Creators (Onde o RESET é usado)
const loadItems = items => ({ type: LOAD_ITEMS, payload: items })
const updateItem = item => ({ type: UPDATE_ITEM, payload: item })
// const deleteItem = id => ({ type: DELETE_ITEM, payload: id })
// const sortItems = () => ({ type: SORT_ITEMS })
const resetItems = () => ({ type: RESET })

export const useChatLogic = (open, initialChatType, initialChatData) => {
  const [tabValue, setTabValue] = useState(0)
  const [showDialog, setShowDialog] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedChat, setSelectedChat] = useState(null)
  const [showMessages, setShowMessages] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [searchParam, setSearchParam] = useState('')

  const [users, dispatch] = useReducer(reducer, [])
  const [chats, chatDispatch] = useReducer(reducer, [])

  const [loadedAvatars, setLoadedAvatars] = useState({})
  const [avatarLoadingStates, setAvatarLoadingStates] = useState({})
  const [usersStatus, setUsersStatus] = useState({})

  const [volume] = useState(localStorage.getItem('volume') || 1)
  const [soundUrls, setSoundUrls] = useState({
    internalChatMessageSoundUrl: null,
    groupMessageSoundUrl: null
  })

  const internalChatSound =
    soundUrls.internalChatMessageSoundUrl || notifySound
  const groupChatSound = soundUrls.groupMessageSoundUrl || internalChatSound

  const [playInternalNotify] = useSound(internalChatSound, { volume })
  const [playGroupNotify] = useSound(groupChatSound, { volume })

  const socketManager = useContext(SocketContext)
  const { user: currentUser } = useContext(AuthContext)

  const getUnreadCount = useCallback(
    item => {
      if (!item) return 0
      if (
        showMessages &&
        (selectedUser?.id === item.id || selectedChat?.id === item.id)
      )
        return 0
      if (item.users)
        return item.users.find(u => u.userId === currentUser?.id)?.unreads || 0
      return item.unreads || item.unreadMessages || 0
    },
    [selectedUser, selectedChat, showMessages, currentUser]
  )

  const handleOpenChat = async user => {
    try {
      const { data: existingChats } = await api.get('/chats')
      let chat = existingChats.records?.find(
        c => c.users.length === 2 && c.users.some(u => u.userId === user.id)
      )
      if (!chat) {
        const { data: newChat } = await api.post('/chats', {
          users: [{ id: user.id }],
          title: `Chat com ${user.name}`
        })
        chat = newChat
      }
      setSelectedUser(user)
      setSelectedChat(null)
      setShowMessages(true)
      if (getUnreadCount(user) > 0) {
        await api.post(`/chats/${chat.id}/read`, { userId: currentUser.id })
        dispatch({ type: UPDATE_ITEM, payload: { ...user, unreads: 0 } })
      }
    } catch (err) {
      toastError(err)
    }
  }

  const handleOpenGroupChat = async chat => {
    setSelectedChat(chat)
    setSelectedUser(null)
    setShowMessages(true)
    if (getUnreadCount(chat) > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, { userId: currentUser.id })
        chatDispatch({ type: UPDATE_ITEM, payload: { ...chat, unreads: 0 } })
      } catch (err) {
        console.error(err)
      }
    }
  }

  useEffect(() => {
    const fetchNotificationSounds = async () => {
      try {
        const { data } = await api.get('/notification-sounds')
        const urls = resolveNotificationSoundUrls(
          data?.sounds || [],
          data?.preferences || {}
        )

        setSoundUrls({
          internalChatMessageSoundUrl: urls.internalChatMessageSoundUrl,
          groupMessageSoundUrl: urls.groupMessageSoundUrl
        })
      } catch (error) {
        if (error?.response?.status !== 401) {
          console.error('Error fetching chat notification sounds:', error)
        }
      }
    }

    fetchNotificationSounds()
  }, [])

  useEffect(() => {
    if (initialChatType && initialChatData && users.length > 0) {
      if (initialChatType === 'user') {
        const user = users.find(u => u.id === initialChatData.id)
        if (user) handleOpenChat(user)
      } else {
        handleOpenGroupChat(initialChatData)
      }
    }
  }, [initialChatType, initialChatData, users.length])

  useEffect(() => {
    dispatch({ type: RESET })
    chatDispatch({ type: RESET })
    setPageNumber(1)
  }, [searchParam])

  useEffect(() => {
    if (!currentUser || !currentUser.id) return
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get('/users/', {
            params: { searchParam, pageNumber }
          })

          const filteredUsers = data.users.filter(
            user => user.id !== currentUser.id
          )

          const { data: chatsData } = await api.get('/chats')

          const processedUsers = filteredUsers.map(user => {
            const userChats = chatsData.records.filter(
              chat =>
                chat.users.length === 2 &&
                chat.users.some(u => u.userId === user.id)
            )

            let totalUnreads = 0
            if (userChats.length > 0) {
              userChats.forEach(chat => {
                const currentUserInChat = chat.users.find(
                  u => u.userId === currentUser.id
                )
                if (currentUserInChat) {
                  totalUnreads += currentUserInChat.unreads || 0
                }
              })
            }

            return {
              ...user,
              unreads: totalUnreads,
              unreadMessages: totalUnreads
            }
          })

          dispatch(loadItems(processedUsers))
          setHasMore(data.hasMore)
          setLoading(false)
        } catch (err) {
          toastError(err)
          setLoading(false)
        }
      }
      fetchUsers()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [searchParam, pageNumber, currentUser])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)
    if (!socket) return

    const handleMessage = (data, eventType) => {
      if (eventType === 'notification' && data.action === 'new-message') {
        // Regra para GRUPOS (mais de 2 usuários)
        if (data.chat && data.chat.users && data.chat.users.length > 2) {
          if (!selectedChat || selectedChat.id !== data.chat.id) {
            const currentUserInChat = data.chat.users.find(
              u => u?.userId === currentUser?.id
            )

            if (
              currentUserInChat &&
              data.newMessage.senderId !== currentUser?.id
            ) {
              const chatIndex = chats.findIndex(c => c.id === data.chat.id)
              const currentUnreads =
                chatIndex !== -1 ? chats[chatIndex].unreads || 0 : 0

              chatDispatch(
                updateItem({
                  ...data.chat,
                  unreads: currentUnreads + 1,
                  lastMessage: data.newMessage.message,
                  updatedAt: new Date().toISOString()
                })
              )
              if (playGroupNotify) playGroupNotify()
            }
          }
        }
        // Regra para CHATS PRIVADOS (2 usuários)
        else if (data.chat && data.chat.users && data.chat.users.length === 2) {
          const otherUser = data.chat.users.find(
            u => u?.userId !== currentUser?.id
          )
          if (otherUser) {
            const userId = otherUser.userId
            if (!selectedUser || selectedUser.id !== userId) {
              if (data.newMessage.senderId !== currentUser?.id) {
                const userIndex = users.findIndex(u => u.id === userId)
                const currentUnreads =
                  userIndex !== -1 ? users[userIndex].unreads || 0 : 0

                dispatch(
                  updateItem({
                    ...(userIndex !== -1
                      ? users[userIndex]
                      : { id: userId, name: otherUser.name }),
                    unreads: currentUnreads + 1,
                    lastMessage: data.newMessage.message,
                    updatedAt: new Date().toISOString()
                  })
                )
                if (playInternalNotify) playInternalNotify()
              }
            }
          }
        }
      }
    }

    socket.on(`company-${companyId}-chat`, data =>
      handleMessage(data, 'notification')
    )
    socket.on('chat-message', data => handleMessage(data, 'chat-message'))
    socket.on('message-sent', data => handleMessage(data, 'message-sent'))

    socket.on(`company-${companyId}-user`, data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: UPDATE_ITEM, payload: data.user })
      }
      if (data.action === 'delete') {
        dispatch({ type: DELETE_ITEM, payload: +data.userId })
      }
    })

    socket.on(`company-${companyId}-chat`, data => {
      if (data.action === 'create' || data.action === 'update') {
        // Lógica de grupo (users.length > 2)
        if (data.record?.users?.length > 2) {
          chatDispatch({ type: UPDATE_ITEM, payload: data.record })
        }
      }
    })

    socket.on('userOnlineChange', data => {
      const { userId, online } = data
      if (userId) {
        setUsersStatus(prev => ({
          ...prev,
          [userId]: !!online
        }))
      }
    })

    return () => {
      socket.off(`company-${companyId}-chat`)
      socket.off(`company-${companyId}-user`)
      socket.off('chat-message')
      socket.off('message-sent')
      socket.off('userOnlineChange')
    }
  }, [
    socketManager,
    currentUser,
    users,
    chats,
    selectedChat,
    selectedUser,
    playGroupNotify,
    playInternalNotify
  ])

  useEffect(() => {
    const fetchUsersStatus = async () => {
      try {
        const { data } = await api.get('/dashboard/status')
        const onlineUsers = {}
        data.usersStatusSummary.forEach(user => {
          onlineUsers[String(user.id)] = !!user.online // Normaliza aqui também
        })
        setUsersStatus(onlineUsers)
      } catch (err) {
        setUsersStatus({})
      }
    }

    fetchUsersStatus()
  }, [])

  useEffect(() => {
    dispatch(resetItems())
    chatDispatch(resetItems())
    setPageNumber(1)
  }, [searchParam])

  // const updateStatus = useCallback(data => {
  //   const { userId, online } = data
  //   if (userId) {
  //     setUsersStatus(prev => ({
  //       ...prev,
  //       [String(userId)]: !!online
  //     }))
  //   }
  // }, [])

  return {
    tabValue,
    showDialog,
    selectedUser,
    selectedChat,
    showMessages,
    loading,
    hasMore,
    searchParam,
    users,
    chats,
    loadedAvatars,
    avatarLoadingStates,
    usersStatus,
    currentUser,
    actions: {
      setTabValue,
      setShowDialog,
      setSelectedUser,
      setSelectedChat,
      setShowMessages,
      setSearchParam,
      setPageNumber,
      handleTabChange: (e, val) => {
        setTabValue(val)
        setShowMessages(false)
        setSearchParam('')
      },
      handleSearch: e => setSearchParam(e.target.value.toLowerCase()),
      handleOpenChat,
      handleOpenGroupChat,
      handleBackToList: () => {
        setShowMessages(false)
        setSelectedUser(null)
        setSelectedChat(null)
      },
      loadUserAvatar: async userId => {
        // Lógica original de fetch avatar transposta para o hook
        if (!userId || avatarLoadingStates[userId] || loadedAvatars[userId]) {
          return
        }

        setAvatarLoadingStates(prev => ({ ...prev, [userId]: true }))

        try {
          const response = await api.get(`/users/${userId}/avatar`)
          if (response.data && response.data.length > 0) {
            const avatarUrl = `${response.data}?cacheBusting=${makeRandomId(5)}`
            setLoadedAvatars(prev => ({
              ...prev,
              [userId]: {
                url: avatarUrl,
                loaded: true,
                error: false
              }
            }))
          } else {
            setLoadedAvatars(prev => ({
              ...prev,
              [userId]: {
                url: null,
                loaded: false,
                error: false
              }
            }))
          }
        } catch (err) {
          console.warn('Falha ao carregar a imagem do usuário', err)
          setLoadedAvatars(prev => ({
            ...prev,
            [userId]: {
              url: null,
              loaded: false,
              error: true
            }
          }))
        } finally {
          setAvatarLoadingStates(prev => ({ ...prev, [userId]: false }))
        }
      },

      handleAvatarError: userId => {
        // Lógica original de erro de avatar
        setLoadedAvatars(prev => ({
          ...prev,
          [userId]: {
            url: null,
            loaded: false,
            error: true
          }
        }))
      },
      dispatch,
      chatDispatch
    },
    helpers: { getUnreadCount }
  }
}
